import {
  type MouseEventHandler,
  type PropsWithChildren,
  type ReactNode,
  useId,
} from 'react'
import { type LinkProps } from 'react-router'
import * as S from './GlassButton.styles'
import {
  type GlassButtonSize,
  type GlassButtonVariant,
} from './GlassButton.styles'

type Props = {
  variant?: GlassButtonVariant
  size?: GlassButtonSize
  onClick?: MouseEventHandler<HTMLElement>
  to?: LinkProps['to']
  state?: LinkProps['state']
  reloadDocument?: boolean
  className?: string
  'data-testid'?: string
}

// Pure CSS/SVG glass. The displacement refraction is Chromium-only; Safari/Firefox
// fall back to blur + tint + shine.
export default function GlassButton({
  variant = 'primary',
  size = 'small',
  onClick,
  to,
  state,
  reloadDocument,
  children,
  ...props
}: PropsWithChildren<Props>) {
  // Unique per instance so multiple buttons don't share one filter id.
  const filterId = `glass-${useId().replace(/:/g, '')}`

  const layers: ReactNode = (
    <>
      <S.Effect style={{ filter: `url(#${filterId})` }} />
      <S.Tint $variant={variant} />
      <S.Shine />
      <S.Text $variant={variant} $size={size}>
        {children}
      </S.Text>
      <S.Filter aria-hidden>
        <filter
          id={filterId}
          x="0%"
          y="0%"
          width="100%"
          height="100%"
          filterUnits="objectBoundingBox"
        >
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.01 0.01"
            numOctaves="1"
            seed="5"
            result="turbulence"
          />
          <feGaussianBlur in="turbulence" stdDeviation="3" result="softMap" />
          <feDisplacementMap
            in="SourceGraphic"
            in2="softMap"
            scale="60"
            xChannelSelector="R"
            yChannelSelector="G"
          />
        </filter>
      </S.Filter>
    </>
  )

  if (to !== undefined) {
    return (
      <S.LinkWrapper
        to={to}
        state={state}
        reloadDocument={reloadDocument}
        onClick={onClick}
        $size={size}
        {...props}
      >
        {layers}
      </S.LinkWrapper>
    )
  }

  return (
    <S.Wrapper type="button" onClick={onClick} $size={size} {...props}>
      {layers}
    </S.Wrapper>
  )
}
